import { observer } from 'mobx-react-lite'
import { ReactElement, useState } from 'react'
import Tree, { ItemId } from 'tree-dnd'
import { useFiles } from 'hooks/queries/finder/useFiles'
import { useActiveFolder } from 'hooks/useActiveFolder'
import { TreeRow } from './TreeRow/TreeRow'
import { useFilesTreeStore } from './stores/FilesTreeStore'
import { toTree } from './treeUtils'
import { reportError } from 'utils/error'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { Loading } from './Loading/Loading'

interface Props {
  workspaceId: number
  location: string
  emptyState?: ReactElement
  queryOptions?: {
    items: number
  }
}

const FilesTree = observer(({ workspaceId, location, emptyState, queryOptions }: Props) => {
  const [isInitialFetch, setIsInitialFetch] = useState(true)
  const treeStore = useFilesTreeStore()
  const filesQuery = useFiles(
    {
      location,
      workspaceId,
      query: queryOptions || { items: 50 },
      disableSchema: true,
    },
    {
      onSuccess: (data) => {
        setIsInitialFetch(false)
        const files = data.pages.map((page) => page.data.files).flat()
        treeStore.setTree(toTree(files, []))
      },
      onError: (error) => {
        setIsInitialFetch(false)
        reportError(error)
        showErrorMessageToast('There was an error fetching your files.')
      },
    },
  )

  const { fetchNextPage, hasNextPage } = filesQuery
  const activeFolder = useActiveFolder()
  const [selectedRows, setSelectedRows] = useState<ItemId>()

  if (!treeStore.tree) return null
  if (isInitialFetch) return <Loading />
  if (emptyState && treeStore.isEmpty) return emptyState

  return (
    <Tree
      tree={treeStore.tree}
      renderItem={(props) => (
        <TreeRow
          {...props}
          isLast={treeStore.isLast(props.item.id)}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          hasMore={hasNextPage}
          loadMore={fetchNextPage}
          updateItem={treeStore.updateItem}
          onRemoveItem={treeStore.removeItem}
          activeFolder={activeFolder}
        />
      )}
      isDragEnabled={false}
      isNestingEnabled={false}
      renderDropPlaceholder={(_) => <></>}
    />
  )
})

export default FilesTree
